/**
 * API Tokens Management Page
 * Create, list, and revoke MCP API tokens for external tool integration
 */

import React, { useEffect, useState } from 'react';
import { tokensApi, ApiToken, CreatedToken } from '../api/tokens';
import { partnerOrganizationsApi, CompanyContext } from '../api/partnerOrganizations';
import { API_BASE_URL } from '../api/config';
const MCP_URL = `${API_BASE_URL.replace(/\/$/, '')}/api/mcp/mindmeld`;

const ApiTokens: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyContext[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [scope, setScope] = useState<'ro' | 'rw'>('ro');
  const [contextError, setContextError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState<CreatedToken | null>(null);
  const [copied, setCopied] = useState(false);
  const [showConfig, setShowConfig] = useState<'claude' | 'cline' | null>(null);

  useEffect(() => {
    loadTokens();
    partnerOrganizationsApi.contexts().then(response => {
      if (!response.success || !response.data) { setContextError(response.error || 'Unable to load organizations'); return; }
      const eligible = response.data;
      setCompanies(eligible);
      if (eligible.length === 1) setCompanyId(eligible[0].company_id);
    }).catch(() => setContextError('Unable to load organizations'));
  }, []);

  const loadTokens = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await tokensApi.list();
      if (response.success && response.data) {
        setTokens(response.data);
      } else {
        setError(response.error || 'Failed to load tokens');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tokens');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId || !companies.some(c => c.company_id === companyId && (c.admin === true || companies.length === 1))) { setError('Select an organization where you are an administrator'); return; }
    setIsCreating(true);
    setError(null);
    try {
      const response = await tokensApi.create({ name: tokenName || 'Default', ...(companies.length === 1 && companies[0].admin !== true ? {} : { company_id: companyId }), scope });
      if (response.success && response.data) {
        setCreatedToken(response.data);
        setIsCreateOpen(false);
        setTokenName('');
        await loadTokens();
      } else {
        setError(response.error || 'Failed to create token');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create token');
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async (tokenId: string) => {
    if (!window.confirm('Revoke this token? Any integrations using it will stop working.')) return;
    try {
      const response = await tokensApi.revoke(tokenId);
      if (response.success) {
        await loadTokens();
      } else {
        setError(response.error || 'Failed to revoke token');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke token');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const activeTokens = tokens.filter(t => t.status === 'active');
  const revokedTokens = tokens.filter(t => t.status === 'revoked');

  const claudeConfig = (token: string) => JSON.stringify({
    mcpServers: {
      mindmeld: {
        type: 'url',
        url: MCP_URL,
        headers: { 'X-MindMeld-Token': token }
      }
    }
  }, null, 2);

  const clineConfig = (token: string) => JSON.stringify({
    mcpServers: {
      mindmeld: {
        url: MCP_URL,
        headers: { 'X-MindMeld-Token': token }
      }
    }
  }, null, 2);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Tokens</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage API tokens for MCP integrations (Claude Code, Cline, etc.)
          </p>
        </div>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-mindmeld-primary hover:bg-opacity-90"
        >
          <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Token
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-md bg-red-50 border border-red-200">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Created Token Banner */}
      {createdToken && (
        <div className="p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-800">Token Created Successfully</h3>
              <p className="mt-1 text-xs text-green-700">
                Copy this token now — it will not be shown again.
              </p>
              <div className="mt-3 flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-white border border-green-300 rounded text-sm font-mono text-gray-900 select-all">
                  {createdToken.token}
                </code>
                <button
                  onClick={() => copyToClipboard(createdToken.token)}
                  className="px-3 py-2 text-sm font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {/* Config snippets */}
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => setShowConfig(showConfig === 'claude' ? null : 'claude')}
                  className="text-xs px-2 py-1 rounded bg-white border border-green-300 text-green-700 hover:bg-green-50"
                >
                  Claude Code Config
                </button>
                <button
                  onClick={() => setShowConfig(showConfig === 'cline' ? null : 'cline')}
                  className="text-xs px-2 py-1 rounded bg-white border border-green-300 text-green-700 hover:bg-green-50"
                >
                  Cline Config
                </button>
              </div>
              {showConfig === 'claude' && (
                <div className="mt-2">
                  <p className="text-xs text-green-700 mb-1">Add to <code>.claude/settings.json</code>:</p>
                  <pre className="p-2 bg-white border border-green-300 rounded text-xs font-mono overflow-x-auto">
                    {claudeConfig(createdToken.token)}
                  </pre>
                </div>
              )}
              {showConfig === 'cline' && (
                <div className="mt-2">
                  <p className="text-xs text-green-700 mb-1">Add to Cline MCP settings:</p>
                  <pre className="p-2 bg-white border border-green-300 rounded text-xs font-mono overflow-x-auto">
                    {clineConfig(createdToken.token)}
                  </pre>
                </div>
              )}
            </div>
            <button
              onClick={() => { setCreatedToken(null); setShowConfig(null); }}
              className="ml-4 text-green-400 hover:text-green-600"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Create Token Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsCreateOpen(false)} />
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-lg sm:w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create API Token</h3>
              <form onSubmit={handleCreate} className="space-y-4">
                {contextError && <p role="alert">{contextError}</p>}
                <label htmlFor="tokenCompany">Organization</label>
                <select id="tokenCompany" value={companyId} onChange={e => setCompanyId(e.target.value)} required>
                  <option value="">Select organization</option>
                  {companies.filter(c => c.admin === true || companies.length === 1).map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
                </select>
                <p className="text-xs text-gray-500">Selecting among multiple organizations requires an administrator entitlement. A single membership uses your existing token permissions.</p>
                <label htmlFor="tokenScope">Access</label>
                <select id="tokenScope" value={scope} onChange={e => setScope(e.target.value as 'ro' | 'rw')}>
                  <option value="ro">Read only</option><option value="rw">Read and write</option>
                </select>
                <div>
                  <label htmlFor="tokenName" className="block text-sm font-medium text-gray-700">
                    Token Name
                  </label>
                  <input
                    id="tokenName"
                    type="text"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    placeholder="e.g., Claude Code, Cline, My Laptop"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-mindmeld-primary focus:ring-mindmeld-primary sm:text-sm"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Give it a name to identify where it's used.
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating || !companyId || !!contextError}
                    className="px-4 py-2 text-sm font-medium text-white bg-mindmeld-primary border border-transparent rounded-md hover:bg-opacity-90 disabled:opacity-50"
                  >
                    {isCreating ? 'Creating...' : 'Create Token'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Active Tokens */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Active Tokens</h2>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary"></div>
          </div>
        ) : activeTokens.length === 0 ? (
          <div className="text-center py-12 px-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No API tokens</h3>
            <p className="mt-1 text-sm text-gray-500">Create a token to connect MCP clients like Claude Code or Cline.</p>
            <button
              onClick={() => setIsCreateOpen(true)}
              className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-mindmeld-primary bg-mindmeld-primary bg-opacity-10 rounded-md hover:bg-opacity-20"
            >
              Create your first token
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activeTokens.map((token) => (
                <tr key={token.token_id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {token.name}
                    <div className="text-xs text-gray-500">{token.company_name || token.company_id || (token.token_plane === 'well' ? 'Personal well' : 'Unbound legacy standard token — replace before use')} · {token.scope || 'Unknown scope'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <code className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">{token.prefix}...</code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {token.last_used ? new Date(token.last_used).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {token.request_count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(token.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      onClick={() => handleRevoke(token.token_id)}
                      className="text-red-600 hover:text-red-900 font-medium"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Revoked Tokens */}
      {revokedTokens.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-500">Revoked Tokens</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revoked</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {revokedTokens.map((token) => (
                <tr key={token.token_id} className="opacity-60">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{token.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    <code className="px-2 py-1 bg-gray-50 rounded text-xs font-mono">{token.prefix}...</code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {token.revoked_at ? new Date(token.revoked_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Setup Guide */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Setup Guide</h2>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Claude Code</h3>
            <p className="text-sm text-gray-600 mb-2">Add to your project's <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">.claude/settings.json</code>:</p>
            <pre className="p-3 bg-gray-900 text-green-400 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "mcpServers": {
    "mindmeld": {
      "type": "url",
      "url": "${MCP_URL}",
      "headers": {
        "X-MindMeld-Token": "YOUR_TOKEN_HERE"
      }
    }
  }
}`}
            </pre>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Cline / Cursor / Windsurf</h3>
            <p className="text-sm text-gray-600 mb-2">
              Install the CLI, then add to your MCP configuration:
            </p>
            <pre className="p-3 bg-gray-900 text-green-400 rounded-md text-xs font-mono overflow-x-auto mb-3">
{`npm install -g @equilateral_ai/mindmeld`}
            </pre>
            <pre className="p-3 bg-gray-900 text-green-400 rounded-md text-xs font-mono overflow-x-auto">
{`{
  "mcpServers": {
    "mindmeld": {
      "command": "mindmeld",
      "args": ["mcp"],
      "env": {
        "MINDMELD_TOKEN": "YOUR_TOKEN_HERE"
      }
    }
  }
}`}
            </pre>
            <p className="text-xs text-gray-500 mt-2">
              Or save your token to <code className="px-1 py-0.5 bg-gray-100 rounded">~/.mindmeld/api-token</code> and omit the env block.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTokens;
